<script>
import { isEmpty } from "lodash";
import { merge } from "lodash";
import { cleanTaxId } from "@/utils/validators";
import userService from "@/services/user";
import companyService from "@/services/company";
import { USER_ROLES } from "@/services/constants";
import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import fileUploadConfigService from "@/services/fileupload-config";
import segmentService from "@/services/segment";
import institutionService from "@/services/institution";
import medicalSpecialtyService from "@/services/medical-specialty";
import UserAddressSection from "@/components/user/UserAddressSection";
import leave from "@/mixins/leave";
import { isObjectLike } from "lodash";
import resourceService from "@/services/resource";
import { theme } from "@/services/config";
import commissionService from "@/services/commission";
import { capitalize } from "@/services/helper";
import {
  DOC_TYPES,
  DOC_TYPE_DEFAULT,
  LANGUAGES,
  TAX_ID_TYPES,
} from "@/services/constants";
import { PROVINCES, OTHER_ENTITIES } from "@/services/argentinaProvinces";

import { enableScores } from "@/services/config";
import medicalInsuranceService from "@/services/medical-insurance";

const {
  ADMIN,
  MANAGER,
  DOCTOR,
  ASSISTANT,
  SCORES,
  PATIENT,
  ADMISSIONIST,
  SECRETARY,
  HEALTH_ADMIN,
} = USER_ROLES;
const CONFIG_TYPE = "users";

export default {
  name: "UserDetail",

  components: {
    Spinner,
    FileUpload,
    UserAddressSection,
  },

  mixins: [leave],

  data() {
    return {
      capitalize,
      languages: LANGUAGES,
      theme,
      docTypes: DOC_TYPES,
      taxIdTypes: TAX_ID_TYPES,
      // BUG-WL688V: 24 provincias + CABA + "Nacional"/"Sin especificar"
      // para el dropdown de Entidad emisora de matrículas (antes era
      // texto libre y el validador de recetas rechazaba por variantes
      // inconsistentes — "BsAs" vs "Bs As" vs "Buenos Aires").
      licensingEntitiesProvinces: PROVINCES,
      licensingEntitiesOther: OTHER_ENTITIES,
      commissions: undefined,
      isLoading: false,
      countries: undefined,
      cities: undefined,
      medicalInsurances: [],
      useScores: enableScores,
      companies: [],
      institutions: [],
      medicalSpecialties: [],
      USER_ROLES,
      configId: this.$route.params.id,
      user: {
        patient: null,
        governmentId: {
          type: DOC_TYPE_DEFAULT,
          number: "",
        },
        taxId: {
          type: "",
          number: "",
        },
        dashExtension: {
          nationalityCity: {
            name: "",
          },
          rfc: "",
          languages: [],
          notificationChannels: {
            high: "",
            medium: "",
            low: "",
          },
        },
        firstName: "",
        lastName: "",
        publicId: "",
        username: "",
        email: "",
        phone: "",
        doctor: {
          dashExtension: {
            degree: {
              name: "",
              degreeLevel: "",
              issuedBy: "",
              issuedAt: "",
            },
          },
          medicalInsuranceObj: {
            insurance: undefined,
            publicInsurance: {
              publicId: "",
              name: "",
            },
            number: "",
            plan: "",
          },
          license: {
            type: "",
            number: "",
            entity: "",
          },
          clinicalEnabled: true,
        },
        role: DOCTOR.id,
        supervisor: null,
        address: {
          street: "",
          city: "",
          district: theme === "dash" ? undefined : "Buenos Aires",
          country: theme === "dash" ? undefined : "Argentina",
          postalCode: "",
          betweenStreets: ["", ""],
        },
      },
      files: [],
      // isUpdate moved to computed
      isFetchingUser: false,
      isPostingUser: false,
      hasAvatar: false,
      usesExternalMedicalInsurance: true,
      isLoadingMedicalInsurances: false,
    };
  },

  computed: {
    roleOptions() {
      let roles = [DOCTOR, MANAGER, SECRETARY, HEALTH_ADMIN];
      if (this.$store.state.user.role === ADMIN.name) {
        roles.push(ADMIN, ASSISTANT, ADMISSIONIST);

        if (enableScores) {
          roles.push(SCORES);
        }
      }

      if (this.isUpdate) {
        let userRole = this.getRoleName(this.user.role);
        // admins and assistants will only have permission to change to admin or assistant
        if (userRole === ADMIN.key || userRole === ASSISTANT.key) {
          roles = roles.filter((role) => {
            return role.key === ADMIN.key || role.key === ASSISTANT.key;
          });
        }
        if (userRole === ADMISSIONIST.key) {
          roles = roles.filter((role) => {
            return (
              role.key === ADMISSIONIST.key ||
              role.key === ADMIN.key ||
              role.key === ASSISTANT.key
            );
          });
        }
      }

      return roles;
    },

    isRoleChangeAllowed() {
      if (this.isProviderMode) return false;
      if (!this.isUpdate) {
        return true;
      }
      const userRole = this.getRoleName(this.user.role);
      if (
        userRole === DOCTOR.key ||
        userRole === MANAGER.key ||
        userRole === PATIENT.key
      ) {
        return false;
      }
      return true;
    },

    sectors() {
      if (!this.user.company) {
        return [];
      }

      const company = this.companies.find((c) => c._id === this.user.company);

      return company?.sectors;
    },

    uploadMediaAvatarEndpoint() {
      return fileUploadConfigService.getUploadAvatarEndpoint(
        CONFIG_TYPE,
        this.configId
      );
    },

    signaturePreview() {
      if (this.user?.doctor?.signature) {
        const mimeType = this.user.doctor.signatureMimeType || "image/png";
        return `data:${mimeType};base64,${this.user.doctor.signature}`;
      }
      return null;
    },

    medicalInsurancePropValue: {
      get() {
        return this.usesExternalMedicalInsurance
          ? this.user.doctor.medicalInsuranceObj.publicInsurance.publicId
          : this.user.doctor.medicalInsurance;
      },
      set(value) {
        if (this.usesExternalMedicalInsurance) {
          this.user.doctor.medicalInsuranceObj.publicInsurance.publicId = value;
        } else {
          this.user.doctor.medicalInsurance = value;
        }
      },
    },
    governmentIdType: {
      get() {
        if (!this.user.governmentId) {
          return DOC_TYPE_DEFAULT;
        }
        return this.user.governmentId.type || DOC_TYPE_DEFAULT;
      },
      set(value) {
        if (!this.user.governmentId) {
          this.$set(this.user, "governmentId", {
            type: value,
            number: "",
          });
        } else {
          this.$set(this.user.governmentId, "type", value);
        }
      },
    },
    isDoctorRole() {
      return (
        this.user.role === this.USER_ROLES.DOCTOR.id ||
        (this.user.role && this.user.role._id === this.USER_ROLES.DOCTOR.id)
      );
    },

    /**
     * @description Indica si se está editando un usuario existente
     * @returns {boolean}
     */
    isUpdate() {
      return (
        this.$route.name === "update-user" ||
        this.$route.name === "providers-edit"
      );
    },

    /**
     * @description Indica si el componente se usa desde el módulo Prestadores
     * @returns {boolean}
     */
    isProviderMode() {
      return !!this.$route.meta?.providerMode;
    },
  },

  created() {
    if (this.isProviderMode && !this.isUpdate) {
      this.user.role = DOCTOR.id;
    }

    this.getUser();
    this.getInstitutions();
    this.getMedicalSpecialties();
    this.getCountries();
    this.getMedicalInsurances();
    this.getCities();
    this.getCommissions();

    if (this.useScores) {
      this.$set(this.user, "company", null);
      this.$set(this.user, "companySector", null);
      this.getCompanies();
    }
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    /**
     * @description Handler del submit del form: aplica los valores por
     *   defecto (`applyFallbacks`) y recién después valida y envía.
     * @param {Function} handleSubmit - handleSubmit del ValidationObserver
     */
    onSubmit(handleSubmit) {
      this.applyFallbacks();
      this.$nextTick(() => handleSubmit(this.submit));
    },

    /**
     * @description Completa campos vacíos con valores derivados de otros
     *   antes de validar. Para sumar otro fallback, agregar su llamada acá.
     */
    applyFallbacks() {
      this.fillLicenseWithTaxId();
    },

    /**
     * @description Si el prestador no cargó matrícula, usa el CUIT/CUIL.
     */
    fillLicenseWithTaxId() {
      const license = this.user.doctor?.license;
      if (!this.isDoctorRole || !license || license.number) return;
      if (!this.user.taxId?.number) return;
      license.number = String(this.user.taxId.number);
      license.type = license.type || "mn";
      license.entity = license.entity || "Sin especificar";
    },

    getRoleName(roleId) {
      for (const role in USER_ROLES) {
        if (USER_ROLES[role].id === roleId) {
          return USER_ROLES[role].key;
        }
      }
    },

    escape(event) {
      if (event.keyCode == 27)
        this.goToRoute(this.isProviderMode ? "providers" : "users-list");
    },

    getManagers() {
      userService.getManagers().then(({ docs }) => (this.suppervisors = docs));
    },

    getCompanies() {
      companyService.get().then(({ docs }) => (this.companies = docs));
    },

    /**
     * @description Handler del submit del form. Si el admin no completó
     *   `username` durante la creación, se autogenera ahora (DNI+iniciales).
     *   Si lo completó manualmente, se respeta tal cual. En update jamás se
     *   regenera (lo evita `generateUsername` con su propio guard).
     * @returns {void}
     */
    submit() {
      if (!this.isUpdate && !this.user.username) {
        this.generateUsername();
      }
      this.createOrUpdateUser();
    },

    async createOrUpdateUser() {
      const eventName = this.isUpdate ? "User Updated" : "User Created";
      segmentService.track({ name: eventName });

      this.isPostingUser = true;
      this.showLeaveGuard = false;

      const serviceCall = this.isUpdate
        ? userService.update
        : userService.create;

      const userRole = this.getRoleName(this.user.role);
      const isDoctorRole = userRole === DOCTOR.key;

      if (!isDoctorRole) {
        delete this.user.doctor;
      } else {
        if (
          !this.user.doctor.institutions &&
          !this.user.doctor.medicalSpecialties &&
          !this.user.doctor.professionalId
        ) {
          delete this.user.doctor;
        } else {
          if (!this.user.doctor.institutions)
            delete this.user.doctor.institutions;
          if (!this.user.doctor.medicalSpecialties)
            delete this.user.doctor.medicalSpecialties;
          if (!this.user.doctor.professionalId)
            delete this.user.doctor.professionalId;
        }
      }

      const userPayload = { ...this.user };
      cleanTaxId(userPayload);

      const formData = new FormData();
      Object.keys(userPayload)
        .filter(
          (key) =>
            !isEmpty(userPayload[key]) || userPayload[key] instanceof File
        )
        .forEach((key) => {
          if (
            isObjectLike(userPayload[key]) &&
            !(userPayload[key] instanceof File)
          ) {
            formData.append(key, JSON.stringify(userPayload[key]));
          } else {
            formData.append(key, userPayload[key]);
          }
        });

      try {
        const { data } = await serviceCall(formData);
        this.$bus.$emit("user-update", data || this.user);

        this.configId = this.configId || data._id;

        if (this.hasAvatar) {
          this.$refs.avatarUpload.startUpload();
        } else {
          this.goToRoute(this.isProviderMode ? "providers" : "users-list");
        }
      } finally {
        this.isPostingUser = false;
      }
    },

    getUser() {
      if (this.isUpdate) {
        this.isFetchingUser = true;
        userService
          .getById(this.$route.params.id)
          .then((user) => {
            // Asegurar que los objetos anidados existen
            if (!user.governmentId) {
              user.governmentId = {
                type: DOC_TYPE_DEFAULT,
                number: "",
              };
            }
            if (!user.taxId) {
              user.taxId = {
                type: "",
                number: "",
              };
            }

            if (user.role && typeof user.role === "object" && user.role._id) {
              user.role = user.role._id;
            }

            this.user = merge({}, this.user, user);
          })
          .finally(() => {
            this.isFetchingUser = false;
          });
      }
    },

    async getMedicalSpecialties() {
      this.isLoading = true;

      medicalSpecialtyService
        .getMedicalSpecialties()
        .then((res) => {
          this.medicalSpecialties = res;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async getInstitutions() {
      this.isLoading = true;

      institutionService
        .getInstitutions()
        .then((res) => {
          this.institutions = res;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onInputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent();
        }
      }
    },

    getModalName() {
      if (this.isProviderMode) {
        return this.isUpdate ? "Actualizar Prestador" : "Nuevo Prestador";
      }
      if (this.isUpdate) {
        return "Actualizar Usuario";
      }
      return "Nuevo Usuario";
    },

    goToRoute(name) {
      this.$router.push({ name });
    },

    handleUploadFiles() {
      this.$refs.fileUpload && this.$refs.fileUpload.startUpload();
    },

    getAvatar() {
      return this.user.avatar && this.user.avatar.key ? [this.user.avatar] : [];
    },

    getAvatarDropdownOptions() {
      return {
        method: "put",
        paramName: "avatar",
        maxFiles: 1,
      };
    },

    onFileUploadSuccess() {
      const avatarCompleted = this.$refs.avatarUpload
        ? this.$refs.avatarUpload.$refs.uploader.dropzone.files.every(
            (file) => file.status === "success" || file.manuallyAdded
          )
        : true;

      if (avatarCompleted) {
        this.goToRoute(this.isProviderMode ? "providers" : "users-list");
      }
    },

    onUploadFilesError() {
      this.isPostingUser = false;
    },

    onAvatarAdded() {
      this.hasAvatar = true;
    },

    onAvatarRemoved() {
      this.hasAvatar = false;
      this.user.avatar = null;
    },

    async onSignatureChange(event) {
      const file = event.target.files[0];
      if (!file) {
        this.user.doctor.signature = null;
        this.user.doctor.signatureMimeType = null;
        return;
      }

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        this.$message.error(
          "Formato de imagen incorrecto. Solo se permiten archivos PNG, JPG o JPEG."
        );
        if (this.$refs.signatureInput) {
          this.$refs.signatureInput.value = "";
        }
        return;
      }
      this.user.doctor.signatureMimeType = file.type;

      // Convertir a base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        const base64Pure = base64String.split(",")[1]; // Solo guardar el base64 puro
        this.user.doctor.signature = base64Pure;
      };
      reader.onerror = () => {
        this.$message.error("Error al leer el archivo");
      };
      reader.readAsDataURL(file);
    },

    removeSignature() {
      this.user.doctor.signature = null;
      this.user.doctor.signatureMimeType = null;
      if (this.$refs.signatureInput) {
        this.$refs.signatureInput.value = "";
      }
    },

    async getCountries() {
      this.isLoading = true;
      try {
        const countries = await resourceService.getCountries();
        this.countries = countries;
      } finally {
        this.isLoading = false;
      }
    },

    async getMedicalInsurances(query) {
      this.isLoadingMedicalInsurances = true;

      try {
        const { docs } = await medicalInsuranceService.get({
          name: { $regex: query, $options: "i" },
        });

        console.log(docs.filter((m) => m._id === "1"));

        const uniqueDocs = docs.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t._id === item._id)
        );
        this.medicalInsurances = uniqueDocs;
      } finally {
        this.isLoadingMedicalInsurances = false;
      }
    },

    async getCities() {
      this.isLoading = true;
      try {
        const cities = await resourceService.getCities();
        this.cities = cities;
      } finally {
        this.isLoading = false;
      }
    },

    async getCommissions() {
      this.isLoading = true;
      try {
        const { docs } = await commissionService.getCommissions();
        this.commissions = docs;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Sugiere un username con la forma `${DNI}${inicialNombre}${inicialApellido}`.
     *   Solo aplica al crear (no al actualizar). Llamado desde `submit()`
     *   cuando el admin deja el campo username vacío — NO se llama en cada
     *   keystroke (regresión BUG: pisaba entrada manual del admin, ver
     *   UC-REG-045).
     * @returns {void}
     */
    generateUsername() {
      if (!this.isUpdate) {
        const govId = this.user.governmentId?.number || "";
        const firstNameLetter =
          this.user.firstName?.charAt(0)?.toLowerCase() || "";
        const lastNameLetter =
          this.user.lastName?.charAt(0)?.toLowerCase() || "";

        if (govId && (firstNameLetter || lastNameLetter)) {
          this.user.username = `${govId}${firstNameLetter}${lastNameLetter}`;
        }
      }
    },
  },

  watch: {
    "user.doctor.medicalInsurance"() {
      if (!this.user.doctor.medicalInsurance) {
        delete this.user.doctor.medicalInsurance;
      }
    },
    "user.governmentId.type"() {
      // Asegurar que el objeto governmentId existe
      if (!this.user.governmentId) {
        this.$set(this.user, "governmentId", {
          type: DOC_TYPE_DEFAULT,
          number: "",
        });
      }
    },
    "user.taxId.type"() {
      // Asegurar que el objeto taxId existe
      if (!this.user.taxId) {
        this.$set(this.user, "taxId", {
          type: "",
          number: "",
        });
      }
    },
    // Watchers de governmentId.number/firstName/lastName que regeneraban
    // username automáticamente fueron eliminados (ver UC-REG-045): pisaban
    // la entrada manual del admin. La sugerencia ahora corre solo en
    // `submit()` si el campo username quedó vacío.
  },
};
</script>

<template lang="pug">
ValidationObserver(v-slot="{handleSubmit}")
  form.modal(:class="{ 'modal--provider': isProviderMode }" @submit.prevent="onSubmit(handleSubmit)")
    header.modal__header
      h2.modal__title {{ getModalName() }}
      .modal__actions
        el-button(type="info" @click="goToRoute(isProviderMode ? 'providers' : 'users-list')") Cancelar
        el-button.border(type="primary" native-type="submit" :loading="isPostingUser") Guardar
    p.modal__subtitle Los campos con (*) son obligatorios
    .modal__content(v-if="isFetchingUser")
      spinner
    .modal__content(v-else)
      // Personal Info Section
      .modal__block
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="person")
            h3.sign__title Información Personal
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label.required Nombre
                ValidationProvider(name="Nombre", rules="required", v-slot="{ errors }")
                  el-input(v-model="user.firstName", autofocus, name="firstName")
                  span.has-error {{ errors[0] }}
              fieldset.modal__field
                label.label.required Apellido
                ValidationProvider(name="Apellido", rules="required", v-slot="{ errors }")
                  el-input(v-model="user.lastName", name="lastName")
                  span.has-error {{ errors[0] }}
              fieldset.modal__field(v-if="theme === 'dash'")
                label.label Segundo Apellido
                el-input(v-model="user.secondLastName")
            .modal__row
              fieldset.modal__field
                label.label ID Proveedor
                el-input(v-model="user.publicId")
              fieldset.modal__field
                label.label.required Usuario
                ValidationProvider(name="Usuario", rules="required", v-slot="{ errors }")
                  el-input(v-model="user.username", :disabled="isUpdate" autocapitalize="none")
                  span.has-error {{ errors[0] }}
            .modal__row
              fieldset.modal__field
                label.label(:class="{ 'required': user.governmentId.type === 'pas' }") Nacionalidad
                ValidationProvider(name="nationality" :rules="user.governmentId.type === 'pas' ? 'required' : ''" v-slot="{errors}")
                  el-select(v-model="user.nationality" placeholder="" filterable clearable default-first-option :disabled="user.governmentId.type !== 'pas'")
                    el-option(
                      v-for="country in countries"
                      :key="country.value"
                      :label="capitalize(country.name)"
                      :value="country.name"
                    )
                  span.has-error {{errors[0]}}
              fieldset.modal__field(v-if='theme === "dash"')
                label.label.required Lugar de Nacimiento
                ValidationProvider(name="nationalityCity" rules="required" v-slot="{errors}")
                  el-select(v-model="user.dashExtension.nationalityCity.name" placeholder="" name="nationalityCity" filterable)
                    el-option(
                      v-for="city in cities"
                      :key="city.value"
                      :label="capitalize(city.name)"
                      :value="city.name"
                    )
                  span.has-error {{errors[0]}}
              fieldset.modal__field
                label.label.required Género
                el-select(v-model="user.gender" placeholder="" default-first-option)
                  el-option(value="m" label="Masculino")
                  el-option(value="f" label="Femenino")
            .modal__row
              fieldset.modal__field
                label.label.required Tipo de Documento
                el-select(v-model="governmentIdType" :disabled="isUpdate"  placeholder="" default-first-option filterable)
                  el-option(v-for="docType in docTypes" :key="docType" :value="docType" :label="docType.toUpperCase()")
              fieldset.modal__field
                label.label.required Número de Documento
                ValidationProvider(name="número de documento" rules="required" v-slot="{errors}")
                  el-input(v-model="user.governmentId.number", name="governmentIdNumberInput")
                  span.has-error {{errors[0]}}
            .modal__row
              fieldset.modal__field
                label.label(:class="{ required: isDoctorRole }") Tipo de Identificación Fiscal
                ValidationProvider(
                  name="Tipo de Identificación Fiscal"
                  :rules="isDoctorRole ? 'required' : ''"
                  v-slot="{ errors }"
                )
                  el-select(v-model="user.taxId.type" placeholder="" default-first-option filterable name="taxIdTypeSelect")
                    el-option(v-for="taxType in taxIdTypes" :key="taxType" :value="taxType" :label="taxType.toUpperCase()")
                  span.has-error {{ errors[0] }}
              fieldset.modal__field
                label.label(:class="{ required: isDoctorRole }") Número de Identificación Fiscal
                ValidationProvider(
                  name="Número de Identificación Fiscal"
                  :rules="isDoctorRole ? 'required' : ''"
                  v-slot="{ errors }"
                )
                  el-input(v-model="user.taxId.number" type="number" placeholder="Sin guiones ni caracteres especiales", name="taxIdNumberInput")
                  span.has-error {{ errors[0] }}
            .modal__row(v-if='theme === "dash"')
              fieldset.modal__field
                label.label Idiomas
                el-select(v-model="user.dashExtension.languages" multiple allow-create)
                  el-option(
                    :key="language"
                    v-for="language in languages"
                    :value="language"
                    :label="language"
                  )
              
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label Foto
                file-upload(
                  ref="avatarUpload",
                  type="image"
                  :url="uploadMediaAvatarEndpoint",
                  :files="getAvatar()",
                  :dropzone-options="getAvatarDropdownOptions()"
                  @file-added="onAvatarAdded",
                  @files-removed="onAvatarRemoved",
                  @fileupload-success="onFileUploadSuccess"
                  @fileupload-error="onUploadFilesError"
                )
      // Contact Section
      .modal__block
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="forum")
            h3.sign__title Contacto
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label.required Email
                ValidationProvider(name="Email", rules="required|email", v-slot="{ errors }")
                  el-input(type="email", v-model="user.email", :disabled="isUpdate", name="email")
                  span.has-error {{ errors[0] }}
            .modal__row
              fieldset.modal__field
                label.label Teléfono
                el-input(v-model="user.phone", name="Teléfono")
              fieldset.modal__field
                label.label WhatsApp
                el-input(v-model="user.secondaryPhone", name="Teléfono")

      .modal__block
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="lock")
            h3.sign__title Seguridad
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label.required Rol
                el-select(v-model="user.role" required :disabled="!isRoleChangeAllowed", name="role-select")
                  el-option(
                    :key="role.key"
                    v-for="role in roleOptions"
                    v-auth="`${role.key}.create`"
                    :value="role.id"
                    :label="role.name"
                  )
              
              //- fieldset.modal__field(v-if="theme === 'dash' && (user.role === USER_ROLES.DOCTOR.id || user.role._id === USER_ROLES.DOCTOR.id)")
              //-   label.label * Comisión
              //-   ValidationProvider(name="Comisión", rules="required", v-slot="{ errors }")
              //-     el-select(
              //-       v-model="user.doctor.commission"
              //-       :disabled="isLoadingSupervisors"
              //-       required
              //-     )
              //-       el-option(
              //-         v-if="commissions && commissions.length"
              //-         v-for="commission in commissions"
              //-         :key="commission._id"
              //-         :value="commission._id"
              //-         :label="`${commission.name} (${commission.percentage}%)`"
              //-       )
              //-     span.has-error {{errors[0]}}

      .modal__block(v-if="user.doctor && user.role === USER_ROLES.DOCTOR.id || user.role._id === USER_ROLES.DOCTOR.id")
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="institution")
            h3.sign__title Instituciones
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label Instituciones
                el-select(v-model="user.doctor.institutions" placeholder="Instituciones" filterable multiple clearable default-first-option)
                  el-option(
                    v-for="i in institutions"
                    :key="i._id"
                    :label="i.name"
                    :value="i._id"
                  )
      .modal__block(v-if="user.doctor && user.role === USER_ROLES.DOCTOR.id || user.role._id === USER_ROLES.DOCTOR.id")
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="medical-specialty")
              h3.sign__title Especialidades médicas
            article.modal__fields
              //- UC-REG-302 — Área / Servicio del médico (encabezado de los PDFs).
              .modal__row(v-if="isDoctorRole")
                fieldset.modal__field
                  label.label Área / Servicio
                  el-input(v-model="user.doctor.service" maxlength="120" placeholder="Ej. Servicio de Cardiología — Hospital San José" name="doctorServiceInput")
              .modal__row(v-if="theme === 'dash'")
                fieldset.modal__field
                  label.label Matrícula
                  ValidationProvider(name="Matrícula" :rules="`min:6|max:8${theme === 'dash' ? '|required': ''}`" v-slot="{ errors }")
                    el-input(v-model="user.doctor.professionalId")
                    span.has-error {{errors[0]}}
              .modal__row(v-else)
                fieldset.modal__field
                  label.label(:class="{ required: isDoctorRole }") Tipo de matrícula
                  ValidationProvider(
                    name="Tipo de matrícula"
                    :rules="isDoctorRole ? 'required' : ''"
                    v-slot="{ errors }"
                  )
                    el-select(v-model="user.doctor.license.type" placeholder="Seleccione un tipo de matrícula" name="matriculaSelect" clearable)
                      el-option(key="mn" value="mn" label="Matrícula Nacional")
                      el-option(key="mp" value="mp" label="Matrícula Provincial")
                    span.has-error {{ errors[0] }}
                fieldset.modal__field
                  label.label(:class="{ required: isDoctorRole }") Número de matrícula
                  ValidationProvider(
                    name="Número de matrícula"
                    :rules="isDoctorRole ? 'required' : ''"
                    v-slot="{ errors }"
                  )
                    el-input(v-model="user.doctor.license.number", name="licenseNumberInput")
                    span.has-error {{ errors[0] }}
                fieldset.modal__field
                  label.label(:class="{ required: isDoctorRole }") Entidad emisora
                  ValidationProvider(
                    name="Entidad emisora"
                    :rules="isDoctorRole ? 'required' : ''"
                    v-slot="{ errors }"
                  )
                    //- BUG-WL688V: dropdown filterable con provincias + opciones
                    //- "Nacional"/"Sin especificar" agrupadas aparte. Usuarios
                    //- con valor histórico no canónico verán el campo vacío al
                    //- editar (deliberado — limpia inconsistencias previas).
                    el-select(
                      v-model="user.doctor.license.entity"
                      name="licenseEntityInput"
                      filterable
                      clearable
                      placeholder="Seleccioná una provincia"
                      style="width: 100%"
                    )
                      el-option-group(label="Provincias")
                        el-option(
                          v-for="province in licensingEntitiesProvinces"
                          :key="province"
                          :label="province"
                          :value="province"
                        )
                      el-option-group(label="Otros")
                        el-option(
                          v-for="entity in licensingEntitiesOther"
                          :key="entity"
                          :label="entity"
                          :value="entity"
                        )
                    span.has-error {{ errors[0] }}
                
              .modal__row
                fieldset.modal__field
                  label.label Especialidades médicas
                  el-select(v-model="user.doctor.medicalSpecialties" placeholder="Especialidades médicas" filterable multiple clearable default-first-option)
                    el-option(
                      v-for="m in medicalSpecialties"
                      :key="m._id"
                      :label="m.name"
                      :value="m._id"
                    )
              .modal__row(v-if="isDoctorRole")
                fieldset.modal__field
                  label.label Gestión clínica
                  el-switch(v-model="user.doctor.clinicalEnabled" active-text="Habilitada" inactive-text="Solo validaciones")

      .modal__block(v-if='theme === "dash"')
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="medical-specialty")
              h3.sign__title Grado académico
            article.modal__fields
              .modal__row
                fieldset.modal__field
                  label.label Nombre
                  el-input(v-model="user.doctor.dashExtension.degree.name")
                fieldset.modal__field
                  label.label Grado de estudio
                  el-input(v-model="user.doctor.dashExtension.degree.degreeLevel")
              .modal__row
                fieldset.modal__field
                  label.label Institución
                  el-input(v-model="user.doctor.dashExtension.degree.issuedBy")
                fieldset.modal__field
                  label.label Finalizado en
                  el-date-picker(lang="es" v-model="user.doctor.dashExtension.degree.issuedAt")

      //- Address section
      template(v-if="isProviderMode && cities && cities.length")
        .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="location_on")
              h3.sign__title Domicilio
            .modal__fields
              user-address-section(v-model="user" :cities="cities" type="user")
      user-address-section(v-else-if="cities && cities.length" v-model="user" :cities="cities" type="user")

      .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="medical-specialty")
              h3.sign__title Firma
            article.modal__fields
              .modal__row
                fieldset.modal__field
                  label.label Firma digital (PNG, JPG o JPEG)
                  input(
                    ref="signatureInput",
                    type="file",
                    accept="image/png,image/jpeg,image/jpg",
                    @change="onSignatureChange"
                  )
                  .signature-preview(v-if="signaturePreview")
                    img(:src="signaturePreview", alt="Firma", style="max-width: 200px; max-height: 100px; margin-top: 10px;")
                    el-button(type="text", @click="removeSignature", style="display: block; margin-top: 5px;") Eliminar firma

      // Company and Sector section
      .modal__block(v-show="useScores && user.role === USER_ROLES.SCORES.id")
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="videogame")
            h3.sign__title Scoring
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label.required Empresa
                el-select(v-model="user.company" placeholder="" filterable clearable default-first-option)
                  el-option(
                    v-for="c in companies"
                    :key="c._id"
                    :label="c.name"
                    :value="c._id"
                  )
</template>

<style lang="scss" scoped>
.role-options {
  max-width: 50%;
}

.label.required::before {
  content: "* ";
  color: #ef4444;
  font-weight: 600;
}

/* ─────────────────────────────────────────────
   Provider mode — diseño de página moderna
   (no afecta el form de usuarios regulares)
───────────────────────────────────────────── */
.modal--provider {
  // Anula el overlay azul fijo
  background: #f1f5f9;
  position: static;
  height: auto;
  padding: 0;
  z-index: auto;
  display: flex;
  flex-direction: column;

  // Barra de acciones: fija en el flujo, no sticky, botones a la derecha
  .modal__header {
    position: static;
    background: transparent;
    border: none;
    padding: 0 0 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .modal__actions {
    display: flex;
    gap: 10px;
    align-items: center;

    // Botón Cancelar
    .el-button--info {
      background: #fff;
      border: 1px solid #e2e8f0;
      color: #475569;
      font-weight: 600;
      border-radius: 8px;
      padding: 10px 20px;

      &:hover {
        background: #f1f5f9;
        border-color: #cbd5e1;
      }
    }

    // Botón Guardar
    .el-button--primary {
      background: #1e3a5f;
      border-color: #1e3a5f;
      color: #fff;
      font-weight: 600;
      border-radius: 8px;
      padding: 10px 20px;

      &:hover {
        background: #162d4a;
        border-color: #162d4a;
      }
    }
  }

  // Título eliminado del header (ya está en el app-topbar)
  .modal__title {
    display: none;
  }

  .modal__subtitle {
    display: none;
  }

  // Contenido: gris, flex column con gap entre cards
  .modal__content {
    background: transparent;
    max-height: none;
    height: auto;
    overflow: visible;
    margin: 20px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  // Cada bloque = card blanca con borde redondeado
  .modal__block {
    background: #fff;
    border: 1px solid #e2e8f0 !important;
    border-radius: 12px;
    overflow: hidden;

    &:not(:last-child) {
      border-bottom: 1px solid #e2e8f0 !important;
    }
    &:last-child {
      flex: none;
    }
  }

  // Sección en columna (header arriba, campos abajo)
  .modal__section {
    flex-direction: column !important;
    height: auto !important;
    width: 100%;
    flex: none;
  }

  // Encabezado de sección — estilo limpio sin ícono (como Nuevo Afiliado)
  .modal__sign {
    flex-direction: row !important;
    min-width: unset !important;
    align-items: center !important;
    justify-content: flex-start !important;
    background: transparent !important;
    border-bottom: 1px solid #e2e8f0;
    padding: 16px 24px;
    gap: 0;

    // Ocultar el ícono circular
    .sign__icon {
      display: none !important;
    }

    // Título: bold navy, tamaño normal, sin uppercase
    .sign__title {
      font-size: 15px !important;
      font-weight: 700 !important;
      color: #1e3a5f !important;
      text-transform: none !important;
      letter-spacing: 0 !important;
      margin: 0 !important;
      text-align: left !important;
    }
  }

  // Campos: padding uniforme, ancho completo
  .modal__fields {
    padding: 20px 24px !important;
    width: 100%;
  }

  // Filas: gap entre campos lado a lado
  .modal__row {
    gap: 14px;
    margin-bottom: 14px;
    flex-wrap: wrap;
    align-items: flex-start;

    &:last-child {
      margin-bottom: 0;
    }

    // Cada fieldset ocupa el espacio disponible equitativamente
    fieldset {
      flex: 1;
      min-width: 180px;
    }
  }
}
</style>
